import { useContext } from 'react'

import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import defaultState, { requiredFields } from '@models/brandGroupMember'
import type { BrandGroupMemberModel, BrandGroupModel } from '@models/types'

import * as brandGroupMemberActions from '@redux/modules/brandGroupMember'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

type BulkCreateBrandGroupMembersParams = {
  bulkCreateParams: { brandGroupId: number, memberIds: number[] },
  dispatch: AppDispatch,
  requestOptions: EntityOptions,
}

const bulkCreateBrandGroupMembers = (params: BulkCreateBrandGroupMembersParams) => {
  const { bulkCreateParams, dispatch, requestOptions } = params
  const { bulkCreateBrandGroupMembers: bulkCreateFn } = brandGroupMemberActions

  return dispatch(bulkCreateFn(bulkCreateParams, requestOptions))
}

type DeleteBrandGroupMemberParams = {
  brandGroupMember: Partial<BrandGroupMemberModel>,
  dispatch: AppDispatch,
}

const deleteBrandGroupMember = (params: DeleteBrandGroupMemberParams) => {
  const { brandGroupMember, dispatch } = params
  const { deleteBrandGroupMember: deleteFn } = brandGroupMemberActions

  // @ts-expect-error TS2345
  return dispatch(deleteFn(brandGroupMember))
}

type CreateBrandGroupMembersParams = {
  brandGroup: Partial<BrandGroupModel>,
  showCreateBrandGroupMembersModal: Function,
}

const addBrandGroupMembers = (params: CreateBrandGroupMembersParams) => new Promise((resolve, reject) => {
  const { brandGroup, showCreateBrandGroupMembersModal } = params

  if (showCreateBrandGroupMembersModal){
    const payload = {
      brandGroup,
    }

    showCreateBrandGroupMembersModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateBrandGroupMembersModal not defined in PageContext callbacks'))
})

const setAdmin = (
  params: {
    brandGroupMember: BrandGroupMemberModel,
    dispatch: AppDispatch,
  },
) => {
  const { brandGroupMember, dispatch } = params
  const { setAdmin: setAdminFn } = brandGroupMemberActions

  return dispatch(setAdminFn(brandGroupMember))
}

export function useBrandGroupMemberForm(
  brandGroupMember: Partial<BrandGroupMemberModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const brandGroupMemberForm = useForm(
    defaultState,
    { entity: brandGroupMember, requiredFields, validateOn },
    [brandGroupMember.id, brandGroupMember.cache_key],
  )

  return {
    ...brandGroupMemberForm,
  }
}

type UseRelationsPayload = {
  brandGroup: Partial<BrandGroupModel>,
}

export function useRelations(brandGroupMember: Partial<BrandGroupMemberModel> = {}): UseRelationsPayload {
  const { brand_group_id } = brandGroupMember

  const brandGroups = useSelector(reduxState => reduxState.entities.brandGroups)

  const brandGroup: Partial<BrandGroupModel> = brand_group_id ? brandGroups[brand_group_id] || {} : {}

  return {
    brandGroup,
  }
}

type UseBrandGroupMemberPayload = {
  brandGroup: BrandGroupModel,
  brandGroupMember: BrandGroupMemberModel,
  callbacks: {
    addBrandGroupMembers: () => void,
    bulkCreateBrandGroupMembers: () => void,
    deleteBrandGroupMember: () => void,
    setAdmin: () => void,
  },
  creating: boolean,
  deleting: boolean,
  loading: boolean,
}

function useBrandGroupMember(initEntity: Partial<BrandGroupMemberModel> = {}): UseBrandGroupMemberPayload {
  // @ts-expect-error TS2345
  const { entity: brandGroupMember } = useLatestEntity(initEntity, 'brandGroupMembers')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateBrandGroupMembersModal } = callbacks || {}

  const {
    creating, deleting, loading,
  } = useSelector(reduxState => reduxState.brandGroupMembers)

  // @ts-expect-error TS2345
  const { brandGroup } = useRelations(brandGroupMember)

  return {
    // @ts-expect-error TS2322
    brandGroup,
    // @ts-expect-error TS2322
    brandGroupMember,
    callbacks: {
      addBrandGroupMembers: () => addBrandGroupMembers({ brandGroup, showCreateBrandGroupMembersModal }),
      // @ts-expect-error TS2322
      bulkCreateBrandGroupMembers: (
        bulkCreateParams: { brandGroupId: number, memberIds: number[] },
        requestOptions: EntityOptions,
      ) => bulkCreateBrandGroupMembers({ bulkCreateParams, dispatch, requestOptions }),
      // @ts-expect-error TS2322
      deleteBrandGroupMember: () => deleteBrandGroupMember({ brandGroupMember, dispatch }),
      // @ts-expect-error TS2322
      setAdmin: () => setAdmin({ brandGroupMember, dispatch }),
    },
    creating,
    deleting,
    loading,
  }
}

export default useBrandGroupMember
